import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { LastPaymentDto, OpenInvoiceDto } from '../../../../common/interfaces/invoicing.interface';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { formatMonthKey, formatSerbianDate, toLocalDateString } from '../../../../utilities/helper/date.helper';

const monthKey = (invoice: OpenInvoiceDto) => `${invoice.periodYear}-${String(invoice.periodMonth).padStart(2, '0')}`;

/** "jul", "sep", or "dec 2025" when it is not this year. */
export const shortMonth = (invoice: OpenInvoiceDto, thisYear = new Date().getFullYear()): string => {
  const name = dayjs(`${monthKey(invoice)}-01`)
    .format('MMM')
    .replace('.', '')
    .toLowerCase();
  return invoice.periodYear === thisYear ? name : `${name} ${invoice.periodYear}`;
};

/**
 * One chip per unpaid month, oldest first. A month past its due date is red; one paid in
 * part says so. The tooltip has the figures, which the row has no room for.
 */
export const MonthChips: React.FC<{ invoices: OpenInvoiceDto[]; credit: number; withChild?: boolean }> = ({
  invoices,
  credit,
  withChild
}) => {
  const { t } = useTranslation();
  const today = toLocalDateString(new Date());
  if (!invoices.length) {
    return (
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        {credit > 0 ? t('balances.allPaidCredit', { amount: formatMoney(credit) }) : t('balances.allPaid')}
      </Typography>
    );
  }
  return (
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.5}>
      {invoices.map(invoice => {
        const late = !!invoice.dueDate && invoice.dueDate < today;
        const partial = invoice.remaining < invoice.totalGross;
        const label = [
          withChild && invoice.childFirstName ? `${invoice.childFirstName}: ` : '',
          shortMonth(invoice),
          partial ? ` · ${t('balances.partly')}` : ''
        ].join('');
        const tip = t('balances.monthTooltip', {
          month: formatMonthKey(monthKey(invoice)).toLowerCase(),
          remaining: formatMoney(invoice.remaining),
          total: formatMoney(invoice.totalGross),
          date: formatSerbianDate(invoice.dueDate)
        });
        return (
          <Tooltip key={invoice.invoiceId} title={withChild && invoice.childFirstName ? `${invoice.childFirstName}: ${tip}` : tip}>
            <Box
              component="span"
              tabIndex={0}
              aria-label={`${label}. ${tip}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 24,
                px: 1,
                borderRadius: 999,
                border: 1,
                borderColor: late ? 'error.main' : 'field.border',
                color: late ? 'error.main' : 'text.primary',
                fontSize: 12,
                whiteSpace: 'nowrap'
              }}
            >
              {label}
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

/**
 * What is owed, and the one fact that decides how urgent it is: how late, or when it
 * falls due - or, with nothing owed, the credit.
 */
export const OwedCell: React.FC<{
  outstanding: number;
  overdueAmount: number;
  daysOverdue: number;
  credit: number;
  invoices: OpenInvoiceDto[];
}> = ({ outstanding, overdueAmount, daysOverdue, credit, invoices }) => {
  const { t } = useTranslation();
  const veryLate = daysOverdue > 30;
  const earliestDue = invoices
    .map(invoice => invoice.dueDate)
    .filter((date): date is string => !!date)
    .sort()[0];
  return (
    <Box sx={{ textAlign: 'right' }}>
      <Typography
        sx={{
          fontSize: 15,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color: veryLate ? 'error.main' : 'text.primary',
          whiteSpace: 'nowrap'
        }}
      >
        {formatMoney(outstanding)}
      </Typography>
      {outstanding <= 0 ? (
        credit > 0 && (
          <Typography sx={{ fontSize: 12, color: 'info.main' }}>{t('balances.inCredit', { amount: formatMoney(credit) })}</Typography>
        )
      ) : daysOverdue > 0 ? (
        <Typography sx={{ fontSize: 12, color: veryLate ? 'error.main' : 'warning.main', whiteSpace: 'nowrap' }}>
          {t('balances.lateDays', { count: daysOverdue })}
          {overdueAmount !== outstanding ? ` · ${t('balances.dueAmount', { amount: formatMoney(overdueAmount) })}` : ''}
        </Typography>
      ) : (
        earliestDue && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {t('balances.dueOn', { date: formatSerbianDate(earliestDue) })}
          </Typography>
        )
      )}
    </Box>
  );
};

/** `dateOnly` where the row says the amounts elsewhere and the column is narrow. */
export const LastPaymentCell: React.FC<{ payment: LastPaymentDto | null; dateOnly?: boolean }> = ({ payment, dateOnly }) => {
  const { t } = useTranslation();
  return payment ? (
    <Box>
      <Typography sx={{ fontSize: 14 }}>{formatSerbianDate(payment.paidAt)}</Typography>
      {!dateOnly && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(payment.amount)}
        </Typography>
      )}
    </Box>
  ) : (
    <Box>
      <Typography sx={{ fontSize: 14, color: 'warning.main' }}>{t('balances.never')}</Typography>
      {!dateOnly && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('balances.noPayments')}</Typography>}
    </Box>
  );
};

/** The payer, and whether a reminder could reach them. */
export const PayerCell: React.FC<{ name: string | null; email: string | null }> = ({ name, email }) => {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography sx={{ fontSize: 14 }}>{name}</Typography>
      {email ? (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{email}</Typography>
      ) : (
        <Typography sx={{ fontSize: 12, color: 'warning.main' }}>{t('balances.noEmail')}</Typography>
      )}
    </Box>
  );
};
